import { PIN } from '../config/pin';
import type { IntoxicationLevel } from '../config/intoxication';
import type { PinChallenge } from '../sim/PinChallenge';
import { formatTime } from '../utils/format';
import { button, el, setHidden, setText } from './dom';
import { pinMotion } from './pinMotion';
import { TaskCheckbox } from './TaskCheckbox';
import { PinFeedback } from './PinFeedback';

/** Mouse-only number row. Hardware number keys do not enter the PIN. */
export class ComputerScreen {
  readonly root = el('section', 'computer is-hidden');
  readonly browserContent = el('div', 'computer__browser-content');
  private readonly feedback = new PinFeedback();
  resultsReady = false;
  private readonly login = el('div', 'computer__login');
  private readonly task = el('div', 'computer__task');
  private readonly taskText = el('span');
  private readonly taskCheck = new TaskCheckbox();
  private readonly timer = el('div', 'computer__timer');
  private readonly entry = el('div', 'computer__entry');
  private readonly status = el('p', 'computer__status');
  private readonly keyboard = el('div', 'computer__keyboard');
  private readonly desktop = el('div', 'computer__desktop');

  constructor(onKey: (key: number) => void) {
    this.root.addEventListener('mousedown', (event) => event.stopPropagation());
    this.root.setAttribute('aria-label', 'Kimmon tietokone');
    this.task.setAttribute('role', 'status');
    this.task.append(this.taskCheck.root, this.taskText);
    this.status.setAttribute('aria-live', 'polite');
    this.entry.setAttribute('aria-label', 'Syötetty PIN-koodi');
    const card = el('div', 'computer__card');
    card.append(el('div', 'computer__avatar', 'K'), el('h1', '', 'Kimmo'),
      el('p', 'computer__hint', 'Kirjaudu sisään PIN-koodilla'), this.entry, this.status);
    const keyboardArea = el('div', 'computer__keyboard-area');
    const rows = [
      ['Esc', 'F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12'],
      ['§', '1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '+', '´', '⌫'],
      ['Tab', 'Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P', 'Å', '¨'],
      ['Caps', 'A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L', 'Ö', 'Ä', 'Enter'],
      ['Shift', '<', 'Z', 'X', 'C', 'V', 'B', 'N', 'M', ',', '.', '-', 'Shift'],
      ['Ctrl', 'Win', 'Alt', 'Välilyönti', 'Alt Gr', 'Fn', 'Ctrl'],
    ];
    for (const labels of rows) {
      const row = el('div', 'computer__key-row');
      for (const label of labels) {
        const numeric = /^\d$/.test(label);
        const active = numeric || label === '⌫';
        const key = active
          ? button(label, () => onKey(numeric ? Number(label) : 10), 'computer__key computer__key--active')
          : el('span', 'computer__key', label);
        if (label.length > 1 || label === '⌫') key.classList.add('computer__key--wide');
        if (label === 'Välilyönti') key.classList.add('computer__key--space');
        if (active) {
          key.tabIndex = -1;
          key.setAttribute('aria-label', label === '⌫' ? 'Poista viimeinen numero' : `Numero ${label}`);
          key.addEventListener('keydown', (event) => {
            if (event.key === 'Enter' || event.key === ' ') event.preventDefault();
          });
        }
        row.append(key);
      }
      this.keyboard.append(row);
    }
    keyboardArea.append(this.keyboard);
    this.login.append(this.task, this.timer, card, keyboardArea,
      el('p', 'computer__instructions', 'Klikkaa numeroita näppäimistöltä.  ⌫ = korjaa  ·  Esc = tauko  ·  R = uusi yritys'));

    const icons = el('div', 'computer__icons');
    for (const [symbol, label] of [['▣', 'Tämä tietokone'], ['♻', 'Roskakori'], ['▰', 'LAN-pelit']]) {
      const icon = el('div', 'computer__icon');
      icon.append(el('span', '', symbol), el('span', '', label));
      icons.append(icon);
    }
    const browser = el('section', 'computer__browser');
    browser.setAttribute('aria-label', 'Google Chrome – Laniaamu');
    const tabs = el('div', 'computer__browser-tabs');
    const tab = el('div', 'computer__browser-tab');
    tab.append(el('span', 'computer__favicon', 'L'), el('span', 'computer__tab-title', 'Laniaamu – Tulokset'));
    const grindrTab = el('div', 'computer__browser-tab computer__browser-tab--inactive');
    const grindrTitle = 'Grindr: The World’s Largest Social Networking App for LGBTQ People.';
    grindrTab.title = grindrTitle;
    grindrTab.append(el('span', 'computer__favicon computer__favicon--grindr', 'G'),
      el('span', 'computer__tab-title', grindrTitle));
    const windowControls = el('span', 'computer__window-controls', '—   □   ×');
    windowControls.setAttribute('aria-hidden', 'true');
    tabs.append(tab, grindrTab, windowControls);
    const toolbar = el('div', 'computer__browser-toolbar');
    const navigation = el('span', 'computer__browser-navigation', '‹  ›  ⟳');
    navigation.setAttribute('aria-hidden', 'true');
    toolbar.append(navigation, el('span', 'computer__address', 'laniaamu.poggers.fi'),
      el('span', 'computer__profile', 'K'));
    browser.append(tabs, toolbar, this.browserContent);
    const bar = el('div', 'computer__taskbar');
    bar.append(el('span', 'computer__start', '⊞  Käynnistä'),
      el('span', 'computer__taskbar-app', 'Google Chrome'), el('span', '', 'Kimmo'));
    const windowLogo = el('div', 'computer__windows');
    windowLogo.setAttribute('aria-hidden', 'true');
    for (let i = 0; i < 4; i++) windowLogo.append(el('i'));
    this.desktop.append(windowLogo, icons, browser, bar);
    this.root.append(this.login, this.desktop);
  }

  mount(parent: HTMLElement): void { parent.append(this.root); }
  setVisible(visible: boolean): void {
    setHidden(this.root, !visible);
  }

  update(pin: PinChallenge, seconds: number, dose: IntoxicationLevel, elapsedMs: number, now: number): void {
    const feedback = this.feedback.update(pin, now);
    this.resultsReady = feedback.results;
    setHidden(this.login, feedback.results);
    setHidden(this.desktop, !feedback.results);
    setText(this.taskText, `Syötä Kimmon pin-koodi: ${pin.code}`);
    this.taskCheck.update(pin.complete);
    setText(this.timer, formatTime(elapsedMs));
    setText(this.entry, pin.input.padEnd(PIN.length, '–').split('').join(' '));
    setText(this.status, feedback.success ? 'Oikea PIN-koodi.'
      : feedback.error ? 'Väärä PIN-koodi. Yritä uudelleen.' : 'Klikkaa neljä numeroa.');
    this.login.classList.toggle('computer__login--error', feedback.error);
    this.login.classList.toggle('computer__login--dimmed', feedback.dimmed);
    this.login.classList.toggle('computer__login--success', feedback.success);
    const motion = pinMotion(seconds, dose);
    this.keyboard.style.transform = `translate(${motion.x}%, ${motion.y}%) rotate(${motion.rotation}deg)`;
  }
}
